import { useEffect, useState } from 'react';

const STORAGE_KEY = 'debug-no-animations';
const NO_ANIMATIONS_CLASS = 'no-animations';

/**
 * Check if animations are currently disabled.
 * Can be called from anywhere (inside or outside React components).
 */
export const isAnimationsDisabled = (): boolean => {
  if (typeof document === 'undefined') return false;
  return document.body.classList.contains(NO_ANIMATIONS_CLASS);
};

/**
 * Debug hook to disable all CSS animations and transitions.
 * Useful for inspecting DOM elements without animation interference.
 *
 * Toggle methods:
 * - URL parameter: Add `?no-animations` to any URL
 * - Keyboard shortcut: Ctrl+Shift+A (Cmd+Shift+A on Mac)
 * - Persists across page refreshes via localStorage
 */
export const useDebugAnimations = (): {
  animationsDisabled: boolean;
  setAnimationsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
} => {
  const [animationsDisabled, setAnimationsDisabled] = useState(() => {
    // Check URL param or localStorage on init
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('no-animations') || localStorage.getItem(STORAGE_KEY) === 'true';
  });

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // Toggle with Ctrl+Shift+A (or Cmd+Shift+A on Mac)
      if (e.key === 'A' && e.shiftKey && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setAnimationsDisabled((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);

  useEffect(() => {
    if (animationsDisabled) {
      document.body.classList.add(NO_ANIMATIONS_CLASS);
      localStorage.setItem(STORAGE_KEY, 'true');
      // eslint-disable-next-line no-console
      console.log('%c🔴 Animations DISABLED for debugging', 'color: #ff6b6b; font-weight: bold;');
    } else {
      document.body.classList.remove(NO_ANIMATIONS_CLASS);
      localStorage.removeItem(STORAGE_KEY);
      // eslint-disable-next-line no-console
      console.log('%c🟢 Animations ENABLED', 'color: #69db7c; font-weight: bold;');
    }
  }, [animationsDisabled]);

  return { animationsDisabled, setAnimationsDisabled };
};

export default useDebugAnimations;
